import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import {
    Download as DownloadIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    TableChart as CsvIcon,
} from '@mui/icons-material'
import type { ArtifactData } from '../types'

interface ArtifactCardProps {
    artifact: ArtifactData
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType: string) {
    if (mimeType.startsWith('image/')) return <ImageIcon sx={{ fontSize: 18 }} />
    if (mimeType.includes('csv')) return <CsvIcon sx={{ fontSize: 18 }} />
    return <FileIcon sx={{ fontSize: 18 }} />
}

export const ArtifactCard: React.FC<ArtifactCardProps> = ({ artifact }) => {
    const [downloading, setDownloading] = useState(false)
    const [expired, setExpired] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const check = () => {
            if (!artifact.expires_at) return
            if (Date.now() > new Date(artifact.expires_at).getTime()) {
                setExpired(true)
            }
        }
        check()
        const interval = setInterval(check, 60_000)
        return () => clearInterval(interval)
    }, [artifact.expires_at])

    const handleDownload = async () => {
        if (expired || downloading) return
        setDownloading(true)
        setError(null)
        try {
            const response = await fetch(artifact.download_url, {
                credentials: 'include',
            })
            if (!response.ok) throw new Error(`Download failed (${response.status})`)
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = artifact.filename
            link.click()
            URL.revokeObjectURL(url)
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Download failed')
        } finally {
            setDownloading(false)
        }
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
                mb: 0.75,
                borderRadius: 1,
                border: '1px solid var(--card-border, #e2e8f0)',
                background: 'var(--card-bg, #fff)',
            }}
        >
            <Box
                sx={{
                    color: 'var(--main-accent, #3b82f6)',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {getFileIcon(artifact.mime_type)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--main-text, #1e293b)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {artifact.filename}
                </Typography>
                <Typography
                    sx={{ fontSize: 10, color: 'var(--main-text-secondary, #64748b)' }}
                >
                    {formatBytes(artifact.size_bytes)}
                    {artifact.description ? ` · ${artifact.description}` : ''}
                    {expired ? ' · Expired' : ''}
                </Typography>
                {error && (
                    <Typography sx={{ fontSize: 10, color: 'var(--error-color, #ef4444)' }}>
                        {error}
                    </Typography>
                )}
            </Box>
            <Button
                size="small"
                variant="outlined"
                disabled={expired || downloading}
                onClick={() => {
                    void handleDownload()
                }}
                startIcon={
                    downloading ? (
                        <CircularProgress size={14} />
                    ) : (
                        <DownloadIcon sx={{ fontSize: 16 }} />
                    )
                }
                sx={{ textTransform: 'none', fontSize: 11, flexShrink: 0 }}
            >
                Download
            </Button>
        </Box>
    )
}

export default ArtifactCard
